import React, { useState } from 'react';
import { View, StyleSheet, Modal, Pressable, Platform, ScrollView, Linking } from 'react-native';
import { useTheme } from '@/theme';
import { ThemedText, Body, Caption } from '@/components/ui/Text';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Ionicons } from '@expo/vector-icons';
import { isMapboxTokenConfigured } from '@/config/mapbox';
import AsyncStorage from '@react-native-async-storage/async-storage';

const SETUP_MODAL_DISMISSED_KEY = '@tripvio_mapbox_setup_modal_dismissed';

type MapboxSetupModalProps = {
  visible: boolean;
  onDismiss: () => void;
  onDontShowAgain?: () => void;
};

export const MapboxSetupModal: React.FC<MapboxSetupModalProps> = ({
  visible,
  onDismiss,
  onDontShowAgain,
}) => {
  const theme = useTheme();
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleDismiss = async () => {
    if (dontShowAgain) {
      await AsyncStorage.setItem(SETUP_MODAL_DISMISSED_KEY, 'true');
      onDontShowAgain?.();
    }
    onDismiss();
  };

  const openSetupGuide = () => {
    // Try to open MAPBOX_SETUP.md or show instructions
    const instructions = 
      'Setup Instructions:\n\n' +
      '1. Create a .env file in the project root\n' +
      '2. Add: EXPO_PUBLIC_MAPBOX_ACCESS_TOKEN=pk.your_token\n' +
      '3. Restart the dev server\n\n' +
      'Get your token from: https://account.mapbox.com/access-tokens/\n\n' +
      'See MAPBOX_SETUP.md for full details.';
    
    if (Platform.OS === 'web') {
      alert(instructions);
    } else {
      const { Alert } = require('react-native');
      Alert.alert('Setup Instructions', instructions);
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleDismiss}
    >
      <View style={[styles.overlay, { backgroundColor: theme.colors.modalOverlay }]}>
        <Card variant="outlined" style={[styles.modal, { backgroundColor: theme.colors.surface }]}>
          <ScrollView showsVerticalScrollIndicator={false}>
            <View style={styles.header}>
              <View style={[styles.iconContainer, { backgroundColor: theme.colors.primary + '15' }]}>
                <Ionicons name="map-outline" size={32} color={theme.colors.primary} />
              </View>
              <ThemedText variant="h2" style={styles.title}>
                Mapbox Setup Required
              </ThemedText>
              <Body color={theme.colors.textSecondary} style={styles.subtitle}>
                To use maps, you need to configure your Mapbox access token.
              </Body>
            </View>

            <View style={styles.content}>
              <View style={styles.step}>
                <View style={styles.stepNumber}>
                  <ThemedText variant="bodyStrong" style={{ color: theme.colors.primary }}>
                    1
                  </ThemedText>
                </View>
                <View style={styles.stepContent}>
                  <ThemedText variant="bodyStrong" style={styles.stepTitle}>
                    Get Your Mapbox Token
                  </ThemedText>
                  <Body color={theme.colors.textSecondary} style={styles.stepDescription}>
                    Sign up at mapbox.com and get your access token from the dashboard.
                  </Body>
                  <Pressable
                    onPress={() => Linking.openURL('https://account.mapbox.com/access-tokens/')}
                    style={styles.linkButton}
                  >
                    <Ionicons name="open-outline" size={16} color={theme.colors.primary} />
                    <Caption color={theme.colors.primary} style={styles.linkText}>
                      Open Mapbox Dashboard
                    </Caption>
                  </Pressable>
                </View>
              </View>

              <View style={styles.step}>
                <View style={styles.stepNumber}>
                  <ThemedText variant="bodyStrong" style={{ color: theme.colors.primary }}>
                    2
                  </ThemedText>
                </View>
                <View style={styles.stepContent}>
                  <ThemedText variant="bodyStrong" style={styles.stepTitle}>
                    Add to .env File
                  </ThemedText>
                  <Body color={theme.colors.textSecondary} style={styles.stepDescription}>
                    Create a .env file in the project root and add:
                  </Body>
                  <View style={[styles.codeBlock, { backgroundColor: theme.colors.background }]}>
                    <Caption style={styles.codeText}>
                      EXPO_PUBLIC_MAPBOX_ACCESS_TOKEN=pk.your_token_here
                    </Caption>
                  </View>
                </View>
              </View>

              <View style={styles.step}>
                <View style={styles.stepNumber}>
                  <ThemedText variant="bodyStrong" style={{ color: theme.colors.primary }}>
                    3
                  </ThemedText>
                </View>
                <View style={styles.stepContent}>
                  <ThemedText variant="bodyStrong" style={styles.stepTitle}>
                    Restart Dev Server
                  </ThemedText>
                  <Body color={theme.colors.textSecondary} style={styles.stepDescription}>
                    Restart your Expo dev server for changes to take effect.
                  </Body>
                </View>
              </View>
            </View>

            <View style={styles.footer}>
              <Pressable
                onPress={() => setDontShowAgain(!dontShowAgain)}
                style={styles.checkboxContainer}
              >
                <Ionicons
                  name={dontShowAgain ? 'checkbox' : 'checkbox-outline'}
                  size={20}
                  color={dontShowAgain ? theme.colors.primary : theme.colors.textSecondary}
                />
                <Caption color={theme.colors.textSecondary} style={styles.checkboxLabel}>
                  Don't show this again
                </Caption>
              </Pressable>
              <View style={styles.buttons}>
                <Button
                  label="View Full Guide"
                  variant="outlined"
                  onPress={openSetupGuide}
                  style={styles.button}
                />
                <Button
                  label="Got It"
                  variant="primary"
                  onPress={handleDismiss}
                  style={styles.button}
                />
              </View>
            </View>
          </ScrollView>
        </Card>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    // Note: backgroundColor is set inline with theme.colors.modalOverlay
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modal: {
    width: '100%',
    maxWidth: 500,
    maxHeight: '80%',
    borderRadius: 16,
    padding: 24,
  },
  header: {
    alignItems: 'center',
    marginBottom: 24,
  },
  iconContainer: {
    width: 64,
    height: 64,
    borderRadius: 32,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    textAlign: 'center',
    marginBottom: 8,
  },
  subtitle: {
    textAlign: 'center',
  },
  content: {
    gap: 20,
    marginBottom: 24,
  },
  step: {
    flexDirection: 'row',
    gap: 16,
  },
  stepNumber: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(19, 42, 19, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  stepContent: {
    flex: 1,
    gap: 4,
  },
  stepTitle: {
    marginBottom: 4,
  },
  stepDescription: {
    marginBottom: 8,
  },
  linkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 4,
  },
  linkText: {
    textDecorationLine: 'underline',
  },
  codeBlock: {
    padding: 12,
    borderRadius: 8,
    marginTop: 8,
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },
  codeText: {
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    fontSize: 12,
  },
  footer: {
    gap: 16,
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  checkboxLabel: {
    flex: 1,
  },
  buttons: {
    flexDirection: 'row',
    gap: 12,
  },
  button: {
    flex: 1,
  },
});

// Helper function to check if setup modal should be shown
export const shouldShowSetupModal = async (): Promise<boolean> => {
  if (__DEV__ && !isMapboxTokenConfigured()) {
    const dismissed = await AsyncStorage.getItem(SETUP_MODAL_DISMISSED_KEY);
    return dismissed !== 'true';
  }
  return false;
};

